import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase/config';
import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs,
} from 'firebase/firestore';
import {
  Box, Typography, Button, TextField, Paper, IconButton,
  Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Chip, ToggleButton, ToggleButtonGroup, LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimerIcon from '@mui/icons-material/Timer';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { generateSessionCode, GROUP_COLORS, QUESTION_TYPES } from '../utils/sessionCode';
import ImageUpload from '../../../components/ImageUpload';
import WorkshopAIContext from '../components/WorkshopAIContext';
import { storage } from '../../../firebase/config';
import { ref, uploadBytesResumable, uploadBytes, getDownloadURL } from 'firebase/storage';
import VideocamIcon from '@mui/icons-material/Videocam';
import LinkIcon from '@mui/icons-material/Link';
import { authFetch } from '../../../utils/authFetch';

function toEmbedUrl(url) {
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) {
    const id = m[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0`;
  }
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1&loop=1&background=1`;
  return null;
}

const TEMPLATES = {
  gruppidentitet: {
    label: 'Gruppidentitet',
    questions: [
      { text: 'Vilka är vi egentligen?', type: 'fritext', hint: 'Vi vet vad vi gör idag. Men vad ÄR vi som yrkesperson? Skriv stödord.', required: true },
      { text: 'Vad behöver vi vara imorgon?', type: 'fritext', hint: 'Om det vi gör idag automatiseras – vad är det som inte går att automatisera? Vad köper kunden egentligen av oss?', required: true },
      { text: 'Vad behöver förändras?', type: 'fritext', hint: 'Vad behöver vi sluta göra, börja göra, eller göra mer av – som team och som individer?', required: true },
    ],
  },
  retrospektiv: {
    label: 'Retrospektiv',
    questions: [
      { text: 'Vad fungerade bra?', type: 'fritext', hint: '', required: true },
      { text: 'Vad fungerade mindre bra?', type: 'fritext', hint: '', required: true },
      { text: 'Vad gör vi annorlunda nästa gång?', type: 'fritext', hint: '', required: true },
      { text: 'Stämningsläge just nu', type: 'skala', hint: '', required: true },
    ],
  },
  prioritering: {
    label: 'Prioriteringsövning',
    questions: [
      { text: 'Vilka tre saker är viktigast att fokusera på?', type: 'fritext', hint: '', required: true },
      { text: 'Vad kan vi deprioritera?', type: 'fritext', hint: '', required: true },
      { text: 'Hur säkra känner vi oss på prioriteringarna?', type: 'skala', hint: '', required: true },
    ],
  },
};

const BTM_PRESET = {
  title: 'BTM Workshop – Vår roll idag och imorgon',
  date: '2026-04-13',
  template: 'gruppidentitet',
  groups: [
    { name: 'Stockholm A', color: GROUP_COLORS[0] },
    { name: 'Stockholm B', color: GROUP_COLORS[1] },
    { name: 'Stockholm C', color: GROUP_COLORS[2] },
    { name: 'Göteborg',    color: GROUP_COLORS[3] },
  ],
};

function newGroup(index) {
  return { id: crypto.randomUUID(), name: `Grupp ${index + 1}`, color: GROUP_COLORS[index % GROUP_COLORS.length] };
}

function newQuestion() {
  return { id: crypto.randomUUID(), text: '', type: 'fritext', hint: '', required: true };
}

const fieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E05500' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#E05500' },
};

export default function WorkshopNDesign() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [title, setTitle]         = useState('');
  const [date, setDate]           = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [wsMode, setWsMode]   = useState('digital'); // 'digital' | 'hybrid'
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [heroEmbedUrl, setHeroEmbedUrl] = useState('');
  const [heroType, setHeroType] = useState('image');
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [initialBrief, setInitialBrief] = useState('');
  const [groups, setGroups]   = useState([newGroup(0), newGroup(1), newGroup(2), newGroup(3)]);
  const [questions, setQuestions] = useState([newQuestion()]);
  const [timerDurations, setTimerDurations] = useState({});
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [inviteUsers, setInviteUsers] = useState([]);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteSelected, setInviteSelected] = useState([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Load existing session
  useEffect(() => {
    if (isNew) return;
    getDoc(doc(db, 'workshops', id)).then(snap => {
      if (!snap.exists()) return navigate('/workshopn');
      const d = snap.data();
      setTitle(d.title || '');
      setDate(d.date || '');
      setWsMode(d.mode || 'digital');
      setImageUrl(d.imageUrl || '');
      setVideoUrl(d.videoUrl || '');
      setHeroEmbedUrl(d.heroEmbedUrl || '');
      setHeroType(d.videoUrl ? 'video' : d.heroEmbedUrl ? 'embed' : 'image');
      setWifiSsid(d.wifiSsid || '');
      setWifiPassword(d.wifiPassword || '');
      setInitialBrief(d.aiContext?.brief || '');
      setCreatedBy(d.createdBy || '');
      setGroups(d.config?.groups || []);
      setQuestions(d.config?.questions || []);
      setTimerDurations(d.timerDurations || {});
      setLoading(false);
    });
  }, [id, isNew, navigate]);

  // Load users for invite (only when editing)
  useEffect(() => {
    if (isNew) return;
    getDocs(collection(db, 'users')).then(snap => {
      setInviteUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [id, isNew]);

  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    if (!t) return;
    setQuestions(t.questions.map(q => ({ ...q, id: crypto.randomUUID() })));
  };

  const applyBTMPreset = () => {
    setTitle(BTM_PRESET.title);
    setDate(BTM_PRESET.date);
    setGroups(BTM_PRESET.groups.map(g => ({ ...g, id: crypto.randomUUID() })));
    applyTemplate(BTM_PRESET.template);
  };

  async function generateThumbnail(file) {
    return new Promise(resolve => {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      const objUrl = URL.createObjectURL(file);
      video.src = objUrl;
      video.onloadeddata = () => { video.currentTime = 0.5; };
      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        URL.revokeObjectURL(objUrl);
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85);
      };
      video.onerror = () => { URL.revokeObjectURL(objUrl); resolve(null); };
    });
  }

  async function handleVideoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setVideoUploading(true);
    setVideoProgress(0);
    const ts = Date.now();
    const ext = file.name.split('.').pop() || 'mp4';
    const basePath = `workshops/${id || 'new'}`;

    // Upload video
    const videoRef = ref(storage, `${basePath}/hero_${ts}.${ext}`);
    const task = uploadBytesResumable(videoRef, file);
    task.on('state_changed',
      snap => setVideoProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      err => { console.error(err); setVideoUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setVideoUrl(url);

        // Generate and upload thumbnail as imageUrl
        try {
          const thumbBlob = await generateThumbnail(file);
          if (thumbBlob) {
            const thumbRef = ref(storage, `${basePath}/hero_thumb_${ts}.jpg`);
            await uploadBytes(thumbRef, thumbBlob);
            const thumbUrl = await getDownloadURL(thumbRef);
            setImageUrl(thumbUrl);
          }
        } catch (_) {}

        setVideoUploading(false);
      }
    );
  }

  const addGroup = () => setGroups(g => [...g, newGroup(g.length)]);
  const removeGroup = (idx) => setGroups(g => g.filter((_, i) => i !== idx));
  const updateGroup = (idx, field, val) => setGroups(g => g.map((x, i) => i === idx ? { ...x, [field]: val } : x));

  const addQuestion = () => setQuestions(q => [...q, newQuestion()]);
  const removeQuestion = (idx) => setQuestions(q => q.filter((_, i) => i !== idx));
  const updateQuestion = (idx, field, val) => setQuestions(q => q.map((x, i) => i === idx ? { ...x, [field]: val } : x));

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const sessionCode = isNew ? generateSessionCode() : undefined;
      const payload = {
        title: title.trim(),
        date,
        mode: wsMode,
        imageUrl: imageUrl || '',
        videoUrl: videoUrl || '',
        heroEmbedUrl: heroEmbedUrl || '',
        wifiSsid: wifiSsid.trim(),
        wifiPassword: wifiPassword.trim(),
        config: { groups, questions },
        timerDurations,
        updatedAt: serverTimestamp(),
      };
      if (isNew) {
        payload.sessionCode = sessionCode;
        payload.createdBy = currentUser.uid;
        payload.createdByName = userProfile?.displayName || userProfile?.name || currentUser.displayName || currentUser.email;
        payload.departmentId = userProfile?.departmentId || '';
        payload.departmentName = userProfile?.departmentName || '';
        payload.createdAt = serverTimestamp();
        payload.status = { active: false, currentQuestion: 0, locked: false, presentMode: false };
        payload.responseCount = 0;
        const ref = doc(collection(db, 'workshops'));
        await setDoc(ref, payload);
        navigate(`/workshopn/${ref.id}/edit`);
      } else {
        await updateDoc(doc(db, 'workshops', id), payload);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvites = async () => {
    if (inviteSelected.length === 0) return;
    setInviteSending(true);
    setInviteResult(null);
    try {
      const emails = inviteSelected.map(uid => {
        const u = inviteUsers.find(x => x.id === uid);
        return u?.email;
      }).filter(Boolean);
      const snap = await getDoc(doc(db, 'workshops', id));
      const sessionCode = snap.data()?.sessionCode || '';
      const workshopUrl = `${window.location.origin}/workshop/${id}`;
      const res = await authFetch('/.netlify/functions/workshop-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emails,
          workshopTitle: title,
          workshopUrl,
          facilitatorName: userProfile?.displayName || userProfile?.name || currentUser?.displayName || '',
          sessionCode,
          message: inviteMessage,
        }),
      });
      let data;
      try { data = await res.json(); } catch { data = { error: `HTTP ${res.status}` }; }
      if (!res.ok && !data.error) data = { error: `HTTP ${res.status}` };
      setInviteResult(data);
      if (data.success) setInviteSelected([]);
    } catch (err) {
      setInviteResult({ error: `Utskicket misslyckades: ${err.message}` });
    } finally {
      setInviteSending(false);
    }
  };

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: '#E05500' }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', p: { xs: 2, md: 4 }, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/workshopn')} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          {isNew ? 'Ny workshop' : 'Redigera workshop'}
        </Typography>
        {isNew && (
          <Button size="small" variant="outlined" onClick={applyBTMPreset}
            sx={{ color: '#E05500', borderColor: '#E05500', textTransform: 'none', fontSize: '0.75rem' }}>
            BTM-förinställning
          </Button>
        )}
        {!isNew && (
          <>
            <Button variant="outlined" onClick={() => navigate(`/workshopn/${id}/schedule`)}
              sx={{ color: '#42A5F5', borderColor: '#42A5F5', textTransform: 'none' }}>
              Körschema
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/workshopn/${id}/intro`)}
              sx={{ color: '#E05500', borderColor: '#E05500', textTransform: 'none' }}>
              Talarstöd
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/workshopn/${id}/present`)}
              sx={{ color: '#4ade80', borderColor: '#4ade80', textTransform: 'none' }}>
              Öppna presentationsläge
            </Button>
          </>
        )}
        <Button variant="contained" onClick={handleSave} disabled={saving || !title.trim()}
          sx={{ bgcolor: '#E05500', '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600, minWidth: 120 }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : isNew ? 'Skapa workshop' : 'Spara'}
        </Button>
      </Box>

      {/* Grundinfo */}
      <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
        <Typography fontWeight={600} mb={2}>Grundinformation</Typography>
        <TextField
          fullWidth label="Titel" value={title} onChange={e => setTitle(e.target.value)}
          sx={{ ...fieldSx, mb: 2 }}
        />
        <TextField
          fullWidth label="Datum" type="date" value={date} onChange={e => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, mb: 2 }}
        />
        {/* Läge */}
        <Typography fontSize="0.78rem" color="text.secondary" mb={1}>Läge</Typography>
        <ToggleButtonGroup
          value={wsMode} exclusive
          onChange={(_, v) => { if (v) setWsMode(v); }}
          size="small"
          sx={{ mb: 1 }}
        >
          <ToggleButton value="digital" sx={{ textTransform: 'none', px: 2, '&.Mui-selected': { bgcolor: '#E05500', color: '#fff', '&:hover': { bgcolor: '#C94D00' } } }}>
            100% Digital
          </ToggleButton>
          <ToggleButton value="hybrid" sx={{ textTransform: 'none', px: 2, '&.Mui-selected': { bgcolor: '#E05500', color: '#fff', '&:hover': { bgcolor: '#C94D00' } } }}>
            Hybrid
          </ToggleButton>
        </ToggleButtonGroup>
        {wsMode === 'hybrid' && (
          <Box sx={{ mt: 1.5, p: 2, bgcolor: '#E0550011', border: '1px solid #E0550033', borderRadius: 1.5 }}>
            <Typography fontSize="0.82rem" fontWeight={700} color="#E05500" mb={1}>
              Hybridläge aktiverat
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
              {[
                { icon: <PrintIcon sx={{ fontSize: 16 }} />,          text: '1. Skriv ut ett blad per fråga och grupp (med QR-kod)' },
                { icon: <QrCodeScannerIcon sx={{ fontSize: 16 }} />,   text: '2. Skanna QR-koden med mobilkameran — ett formulär öppnas direkt i telefonen' },
                { icon: <BorderColorIcon sx={{ fontSize: 16 }} />,     text: '3. Fysiska deltagare skriver sina svar på bladet' },
                { icon: <CameraAltIcon sx={{ fontSize: 16 }} />,       text: '4. Fotografera hela bladet i formuläret (text + QR ska synas)' },
                { icon: <AutoAwesomeIcon sx={{ fontSize: 16 }} />,     text: '5. Granska AI-tolkade svar och tryck Spara' },
              ].map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  {step.icon}
                  <Typography fontSize="0.78rem">{step.text}</Typography>
                </Box>
              ))}
            </Box>
            {!isNew && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => navigate(`/workshopn/${id}/print`)}
                sx={{ color: '#E05500', borderColor: '#E05500', textTransform: 'none', fontSize: '0.78rem' }}
              >
                Skriv ut hybridblad
              </Button>
            )}
            {isNew && (
              <Typography fontSize="0.72rem" color="text.secondary">
                Spara workshopen först för att kunna skriva ut hybridblad.
              </Typography>
            )}
          </Box>
        )}

        {/* Välkomstmedia */}
        <Box sx={{ mt: 2.5 }}>
          <Typography fontSize="0.78rem" color="text.secondary" mb={1}>
            Välkomstbild / -video (valfritt — visas på välkomstsliden)
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={heroType}
            onChange={(_, v) => {
              if (!v || v === heroType) return;
              setImageUrl(''); setVideoUrl(''); setHeroEmbedUrl('');
              setHeroType(v);
            }}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="image" sx={{ textTransform: 'none', fontSize: '0.72rem', px: 1.5 }}>
              <CameraAltIcon sx={{ fontSize: 14, mr: 0.5 }} />Bild
            </ToggleButton>
            <ToggleButton value="video" sx={{ textTransform: 'none', fontSize: '0.72rem', px: 1.5 }}>
              <VideocamIcon sx={{ fontSize: 14, mr: 0.5 }} />Video
            </ToggleButton>
            <ToggleButton value="embed" sx={{ textTransform: 'none', fontSize: '0.72rem', px: 1.5 }}>
              <LinkIcon sx={{ fontSize: 14, mr: 0.5 }} />YouTube / Vimeo
            </ToggleButton>
          </ToggleButtonGroup>

          {heroType === 'image' && (
            <Box>
              {imageUrl && (
                <Box component="img" src={imageUrl} alt="Omslagsbild"
                  sx={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1, mb: 1.5 }} />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <ImageUpload
                  currentUrl={imageUrl}
                  onUpload={url => setImageUrl(url)}
                  path={`workshops/${id || 'new'}`}
                  label={imageUrl ? 'Byt bild' : 'Ladda upp bild'}
                />
                {imageUrl && (
                  <Button size="small" onClick={() => setImageUrl('')} sx={{ color: 'error.main', textTransform: 'none' }}>
                    Ta bort
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {heroType === 'video' && (
            <Box>
              {videoUrl && (
                <Box component="video" src={videoUrl} controls muted playsInline
                  sx={{ width: '100%', maxHeight: 180, borderRadius: 1, mb: 1.5 }} />
              )}
              {videoUploading && (
                <Box sx={{ mb: 1.5 }}>
                  <LinearProgress variant="determinate" value={videoProgress}
                    sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#E05500' } }} />
                  <Typography fontSize="0.7rem" color="text.secondary" mt={0.5}>{videoProgress}%</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component="label" variant="outlined" size="small"
                  startIcon={videoUploading ? <CircularProgress size={14} /> : <VideocamIcon />}
                  disabled={videoUploading}
                  sx={{ color: 'text.primary', borderColor: 'divider', textTransform: 'none' }}>
                  {videoUrl ? 'Byt video' : 'Ladda upp video'}
                  <input type="file" hidden accept="video/*" onChange={handleVideoUpload} />
                </Button>
                {videoUrl && (
                  <Button size="small" onClick={() => setVideoUrl('')} sx={{ color: 'error.main', textTransform: 'none' }}>
                    Ta bort
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {heroType === 'embed' && (
            <Box>
              <TextField
                size="small" fullWidth
                placeholder="https://www.youtube.com/watch?v=... eller https://vimeo.com/..."
                value={heroEmbedUrl}
                onChange={e => setHeroEmbedUrl(e.target.value)}
                sx={{ mb: 0.75 }}
              />
              {heroEmbedUrl && !toEmbedUrl(heroEmbedUrl) && (
                <Typography fontSize="0.72rem" color="error.main">
                  Ange en giltig YouTube- eller Vimeo-länk.
                </Typography>
              )}
              {heroEmbedUrl && toEmbedUrl(heroEmbedUrl) && (
                <Typography fontSize="0.72rem" sx={{ color: 'success.main' }}>
                  ✓ Giltig länk — spelas automatiskt på välkomstsliden.
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {/* WiFi-info */}
        <Box sx={{ mt: 2.5 }}>
          <Typography fontSize="0.78rem" color="text.secondary" mb={1}>
            WiFi-info till deltagarna (valfritt — visas på välkomstsliden och körschema-HDMI)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small" label="Nätverk (SSID)" value={wifiSsid}
              onChange={e => setWifiSsid(e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 160 }}
            />
            <TextField
              size="small" label="Lösenord" value={wifiPassword}
              onChange={e => setWifiPassword(e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 160 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Mallar */}
      {isNew && (
        <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
          <Typography fontWeight={600} mb={2}>Välj mall (valfritt)</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(TEMPLATES).map(([key, t]) => (
              <Chip
                key={key}
                label={t.label}
                onClick={() => applyTemplate(key)}
                sx={{
                  bgcolor: 'action.selected', color: 'text.secondary', cursor: 'pointer',
                  '&:hover': { bgcolor: '#E05500', color: '#fff' },
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Grupper */}
      <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography fontWeight={600} sx={{ flex: 1 }}>Grupper</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addGroup}
            sx={{ color: '#E05500', textTransform: 'none' }}>
            Lägg till
          </Button>
        </Box>
        {groups.map((g, i) => (
          <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <input
              type="color"
              value={g.color || '#E05500'}
              onChange={e => updateGroup(i, 'color', e.target.value)}
              style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 4 }}
            />
            <TextField
              size="small" fullWidth
              value={g.name} onChange={e => updateGroup(i, 'name', e.target.value)}
              placeholder={`Grupp ${i + 1}`}
              sx={fieldSx}
            />
            <IconButton onClick={() => removeGroup(i)} disabled={groups.length === 1}
              sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Paper>

      {/* Frågor */}
      <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography fontWeight={600} sx={{ flex: 1 }}>Frågor</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addQuestion}
            sx={{ color: '#E05500', textTransform: 'none' }}>
            Lägg till
          </Button>
        </Box>
        {questions.map((q, i) => (
          <Box key={q.id} sx={{ mb: 3, pb: 3, borderBottom: i < questions.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography sx={{ color: '#E05500', fontWeight: 700, fontSize: '0.8rem', minWidth: 24 }}>
                {i + 1}.
              </Typography>
              <TextField
                size="small" fullWidth
                value={q.text} onChange={e => updateQuestion(i, 'text', e.target.value)}
                placeholder="Frågetext"
                sx={fieldSx}
              />
              <FormControl size="small" sx={{ minWidth: 130, ...fieldSx }}>
                <InputLabel>Typ</InputLabel>
                <Select label="Typ" value={q.type} onChange={e => updateQuestion(i, 'type', e.target.value)}
                  sx={{ color: 'text.primary' }}>
                  {QUESTION_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Timer per fråga */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
                px: 1, py: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                bgcolor: 'action.hover',
              }}>
                <TimerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Box
                  component="input" type="number" min={1} max={60}
                  value={Math.round((timerDurations[i] ?? 300) / 60)}
                  onChange={e => {
                    const v = Math.max(1, Math.min(60, parseInt(e.target.value) || 5));
                    setTimerDurations(td => ({ ...td, [i]: v * 60 }));
                  }}
                  style={{ width: 34, textAlign: 'center', fontSize: '0.8rem',
                    background: 'transparent', border: 'none', outline: 'none', color: 'inherit' }}
                />
                <Typography fontSize="0.7rem" color="text.secondary">min</Typography>
              </Box>
              <IconButton onClick={() => removeQuestion(i)} disabled={questions.length === 1}
                sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              size="small" fullWidth multiline minRows={2}
              value={q.hint || ''} onChange={e => updateQuestion(i, 'hint', e.target.value)}
              placeholder="Ledtext / beskrivning (valfritt)"
              sx={{ ...fieldSx, pl: 3.5, mb: q.type === 'flerval' ? 1 : 0 }}
            />
            {q.type === 'flerval' && (
              <TextField
                size="small" fullWidth
                value={(q.options || []).join('\n')}
                onChange={e => updateQuestion(i, 'options', e.target.value.split('\n').filter(Boolean))}
                placeholder={'Svarsalternativ – ett per rad\nt.ex.\nInstämmer helt\nInstämmer delvis\nInstämmer inte'}
                multiline minRows={3}
                sx={{ ...fieldSx, pl: 3.5 }}
                helperText="Ange ett svarsalternativ per rad"
              />
            )}
          </Box>
        ))}
      </Paper>

      {/* Bjud in deltagare */}
      {!isNew && (
        <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EmailIcon sx={{ color: '#E05500', fontSize: 20 }} />
            <Typography fontWeight={600}>Bjud in deltagare</Typography>
          </Box>
          <TextField
            size="small" fullWidth
            placeholder="Sök namn eller affärsområde..."
            value={inviteSearch}
            onChange={e => setInviteSearch(e.target.value)}
            sx={{ ...fieldSx, mb: 1.5 }}
          />
          <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
            {inviteUsers
              .filter(u => {
                const q = inviteSearch.toLowerCase();
                return !q || (u.displayName || u.name || u.email || '').toLowerCase().includes(q)
                  || (u.departmentName || '').toLowerCase().includes(q);
              })
              .map(u => {
                const selected = inviteSelected.includes(u.id);
                return (
                  <Box
                    key={u.id}
                    onClick={() => setInviteSelected(s =>
                      selected ? s.filter(x => x !== u.id) : [...s, u.id]
                    )}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                      cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                      bgcolor: selected ? '#E0550011' : 'transparent',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: selected ? '#E0550022' : 'action.hover' },
                    }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: selected ? '#E05500' : 'text.disabled', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontSize="0.88rem" noWrap>{u.displayName || u.name || u.email}</Typography>
                      <Typography fontSize="0.72rem" color="text.secondary" noWrap>{u.departmentName || u.email}</Typography>
                    </Box>
                  </Box>
                );
              })
            }
          </Box>
          {inviteSelected.length > 0 && (
            <Typography fontSize="0.78rem" color="text.secondary" mb={1.5}>
              {inviteSelected.length} person{inviteSelected.length !== 1 ? 'er' : ''} vald{inviteSelected.length !== 1 ? 'a' : ''}
            </Typography>
          )}
          <TextField
            size="small" fullWidth multiline minRows={2}
            placeholder="Personligt meddelande (valfritt)..."
            value={inviteMessage}
            onChange={e => setInviteMessage(e.target.value)}
            sx={{ ...fieldSx, mb: 1.5 }}
          />
          <Button
            variant="contained"
            onClick={handleSendInvites}
            disabled={inviteSending || inviteSelected.length === 0}
            startIcon={inviteSending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <EmailIcon />}
            sx={{ bgcolor: '#E05500', '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
          >
            {inviteSending ? 'Skickar...' : `Skicka inbjudan${inviteSelected.length > 1 ? ` (${inviteSelected.length})` : ''}`}
          </Button>
          {inviteResult && (
            <Box mt={1.5}>
              <Typography fontSize="0.8rem" color={inviteResult.success ? 'success.main' : 'error.main'}>
                {inviteResult.success
                  ? `✓ ${inviteResult.sent} inbjudan${inviteResult.sent !== 1 ? 'ar' : ''} skickad${inviteResult.sent !== 1 ? 'e' : ''}.`
                  : inviteResult.error || 'Något gick fel.'}
              </Typography>
              {!inviteResult.success && inviteResult.errors?.length > 0 && (
                <Typography fontSize="0.72rem" color="text.secondary" mt={0.5} sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {inviteResult.errors.map(e => `${e.email}: ${e.error}`).join('\n')}
                </Typography>
              )}
              {!inviteResult.success && (inviteResult.error || '').includes('not configured') && (
                <Typography fontSize="0.73rem" color="text.secondary" mt={0.5}>
                  Tips: kontrollera att <code>RESEND_API_KEY</code> är inlagd under Netlify → Site settings → Environment variables.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* AI-kontext */}
      {!isNew && (
        <WorkshopAIContext
          workshopId={id}
          initialBrief={initialBrief}
          isOwner={!createdBy || createdBy === currentUser?.uid}
        />
      )}

      {/* Spara */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {!isNew && (
          <>
            <Button
              variant="outlined"
              onClick={() => navigate(`/workshopn/${id}/schedule`)}
              sx={{ color: '#42A5F5', borderColor: '#42A5F5', textTransform: 'none' }}
            >
              Körschema
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/workshopn/${id}/intro`)}
              sx={{ color: '#E05500', borderColor: '#E05500', textTransform: 'none' }}
            >
              Talarstöd
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/workshopn/${id}/present`)}
              sx={{ color: '#4ade80', borderColor: '#4ade80', textTransform: 'none' }}
            >
              Öppna presentationsläge
            </Button>
          </>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !title.trim()}
          sx={{ bgcolor: '#E05500', '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600, minWidth: 120 }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : isNew ? 'Skapa workshop' : 'Spara'}
        </Button>
      </Box>
    </Box>
  );
}
